import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useStore } from 'jotai';
import { ChevronDown, Maximize2, Minimize2, X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useTranslation } from 'react-i18next';

import { alignmentsAtom, alignmentWalkAtom, DEFAULT_WALK_STEP } from '@/atoms/alignment.atom';
import { clearanceEnvelopeVisibleAtom, clearanceViewModeAtom } from '@/atoms/clearance.atom';
import { layerDefaultSettings, layerSettingsAtom, layersAtom, type tLayerSettings } from '@/atoms/layers.atom';
import { usePointcloudHasClassification } from '@/atoms/pointcloudCapabilities.atom';
import {
  DOCK_HEIGHT_MAX,
  DOCK_HEIGHT_MIN,
  DOCK_VERY_NARROW_PX,
  stationClipToBinAtom,
  stationVolumeDockAtom,
  stationVolumeDockExpandedAtom,
  stationVolumeDockParamsVisibleAtom,
  stationVolumeSelectionAtom,
} from '@/atoms/stationVolumes.atom';
import { clipBoxStateAtom, type ClipBoxState } from '@/atoms/viewer.atom';
import { ClearanceChart } from '@/components/ClearanceChart';
import { ClearanceSectionChart } from '@/components/ClearanceSectionChart';
import { ClearanceProfileSketch } from '@/components/ClearanceProfileSketch';
import { ClearanceTable } from '@/components/ClearanceTable';
import { DockChip, DockKpi } from '@/components/dock/DockHeaderParts';
import { UpgradeBadge } from '@/components/subscription/FeatureGate';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LoadingBadge, LoadingBadgeKeyframes } from '@/components/ui/LoadingBadge';
import { useClearance, useClearanceByproductUrl } from '@/hooks/useClearance';
import { useFeature } from '@/hooks/useFeature';
import { clipBoxAtStation, internalToDisplayRaw, type Alignment, type ClipDims } from '@/core/alignment';
import { clearanceClipDims, clearanceWorstTarget } from '@/lib/clearance/bands';
import type { ClearanceMetric } from '@/lib/clearance/chart';
import type { ClearanceBin, DerivedClearanceLayerMetadata } from '@/lib/clearance/types';
import {
  CLEARANCE_VIEW_MODES,
  releaseViewMode,
  takeOverViewMode,
  viewModeUnavailableReason,
  type ClearanceViewMode,
  type ViewModeUnavailableReason,
} from '@/lib/clearance/viewMode';
import { binCentreStation, binIndexForStation } from '@/lib/stationVolumes/aggregate';
import { alignmentHashMatches } from '@/lib/stationVolumes/alignmentInput';
import { formatFixed, formatStationRange, staTag } from '@/lib/stationVolumes/format';
import { SV_TOKENS } from '@/lib/stationVolumes/tokens';

export interface ClearanceDockProps {
  /**
   * Which project's clip box "Clip to station" takes over
   * (`clipBoxStateAtom` is an atomFamily keyed by project, and the box is
   * persisted per project). It was carried unread until Task 6 for symmetry
   * with `StationVolumeDock`; it is load-bearing now, so it is required.
   */
  projectId: string;
}

/**
 * The one field this dock reads out of `useLayerSettings`' cached
 * `['layer-settings', id]` query — the renderer's own resolution of a layer's
 * remote settings (finding M9). Declared structurally rather than imported
 * from the generated operation types so that reading the cache pulls in no
 * GraphQL document; `getQueryData` is a typed cast either way.
 */
interface CachedLayerSettings {
  layer?: { settings?: tLayerSettings | null } | null;
}

/** Arrow-key resize step, matching the sibling dock's grip. */
const RESIZE_KEY_STEP = 24;
const clampHeight = (h: number) => Math.max(DOCK_HEIGHT_MIN, Math.min(DOCK_HEIGHT_MAX, h));

/** Plot height used until the ResizeObserver reports — jsdom, and the first
 *  paint before layout has run. The dock's default body height minus its
 *  chrome; the measured value replaces it on the very next frame. */
const DEFAULT_PLOT_H = 180;

const TABS = ['chart', 'table', 'profile'] as const;

/** Label and tooltip per render mode. A map rather than a template string so
 *  every key is greppable and the locale parity test can see all six. */
const VIEW_MODE_KEYS = {
  scalar: { label: 'clearance.panel.viewModeScalar', help: 'clearance.panel.viewModeScalarHelp' },
  highlight: { label: 'clearance.panel.viewModeHighlight', help: 'clearance.panel.viewModeHighlightHelp' },
  only: { label: 'clearance.panel.viewModeOnly', help: 'clearance.panel.viewModeOnlyHelp' },
  // `as const` so the keys stay literals and `t()` can still check them —
  // widening them to `string` is what makes i18next's typed `t` reject the
  // lookup (the same shape `CLEARANCE_LEGEND_KEYS` uses).
} as const satisfies Record<ClearanceViewMode, { label: string; help: string }>;

/** Why Highlight / Violations-only cannot be offered, in the reader's words.
 *  A literal map for the same reason `VIEW_MODE_KEYS` is one — a template
 *  literal key does not typecheck against i18next's generated key union. */
const VIEW_MODE_UNAVAILABLE_KEYS = {
  loading: 'clearance.panel.viewModeUnavailable.loading',
  noClassification: 'clearance.panel.viewModeUnavailable.noClassification',
} as const satisfies Record<ViewModeUnavailableReason, string>;

/**
 * The clearance dock — a parallel component to `StationVolumeDock`, not a
 * `kind` prop on it (ruling R2). It reuses the shared dock atoms (so only one
 * dock is ever open and the height preference carries across), the `SV_TOKENS`
 * palette and the station formatters; it does NOT reuse the volume-typed
 * chart, table or section machinery.
 *
 * Tabs are Chart / Table / **Profile**. There is deliberately no Section tab:
 * the station Section tab is built on `station_offsets.f32`, which a clearance
 * run does not produce (ruling R1). The Profile tab is the spec's own named
 * fallback and carries the same information the overlay was for — where in the
 * envelope the worst point sat.
 */
export const ClearanceDock = ({ projectId }: ClearanceDockProps) => {
  const { t } = useTranslation('project');
  const [dock, setDock] = useAtom(stationVolumeDockAtom);
  const [expanded, setExpanded] = useAtom(stationVolumeDockExpandedAtom);
  const [paramsOn, setParamsOn] = useAtom(stationVolumeDockParamsVisibleAtom);
  const [selection, setSelection] = useAtom(stationVolumeSelectionAtom);
  const layers = useAtomValue(layersAtom);
  const alignments = useAtomValue(alignmentsAtom);
  const [width, setWidth] = useState(1200);
  // Lifted out of `ClearanceChart` so switching to Table and back does not
  // silently reset the reader's chosen metric to counts (L6).
  const [metric, setMetric] = useState<ClearanceMetric>('count');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [plotBox, setPlotBox] = useState<{ w: number; h: number } | null>(null);
  const gate = useFeature('analysis.clearance');
  // "Clip to station" takes the viewer's own clip box over, so it needs that
  // feature's tier as well as this analysis's — whichever half is locked is
  // the one the badge names.
  const clipGate = useFeature('viewer.clipBox');
  const [walk, setWalk] = useAtom(alignmentWalkAtom);
  const [clipToBin, setClipToBin] = useAtom(stationClipToBinAtom(dock?.layerId ?? '__none'));
  // Keyed by layer, like the volumes bands: a preference set on one run must
  // not follow the reader to the next one. `__none` is the atomFamily's key
  // while no dock is open — the hook cannot be called conditionally.
  const [envelopeOn, setEnvelopeOn] = useAtom(clearanceEnvelopeVisibleAtom(dock?.layerId ?? '__none'));

  const isClearance = dock?.dataset === 'clearance';
  const layer = layers.find((l) => l.id === dock?.layerId);
  const layerMeta = (layer?.metadata ?? null) as DerivedClearanceLayerMetadata | null;
  const meta = layerMeta?.clearance ?? null;
  const batchId = layerMeta?.batchId;

  // "Which points violated" — the render mode, and the settings take-over that
  // shows it. The target is the DERIVED cloud (`dock.layerId`): that is the
  // layer carrying classification 13, not `meta.pointcloudLayerId`, which is
  // the untouched source survey the run was scored against (ruling P5).
  const store = useStore();
  const viewLayerId = isClearance && dock ? dock.layerId : null;
  const viewKey = viewLayerId ?? '__none';
  const [viewState, setViewState] = useAtom(clearanceViewModeAtom(viewKey));
  // Read/write the local OVERRIDE atom directly rather than through
  // `useLayerSettings`: that hook fires a per-layer GraphQL query, and this
  // component stays mounted with no dock open. `layersAtom` already carries
  // the remote settings, so the same `local || remote || default` resolution
  // costs nothing here.
  const [localSettings, setLocalSettings] = useAtom(layerSettingsAtom(viewKey));
  // Finding M9: the base a mode patches has to be the settings that are
  // ACTUALLY on screen, and the renderer (`Pointcloud` → `useLayerSettings`)
  // resolves those as `local || remote || default` where `remote` is the FULL
  // per-layer `layer-settings` query — not `layersAtom`'s selection, which is
  // a documented subset with the image-only fields left out.
  //
  // So read the renderer's own resolution out of the react-query cache under
  // its own key. A cache READ, deliberately: subscribing with `useQuery` would
  // fire a per-layer request from a component that stays mounted with no dock
  // open, and the value is only ever needed at the instant of a take-over.
  // `Pointcloud` has already populated it for any layer that is on screen; when
  // it has not (a hidden layer, a cold cache), the old merge is still the
  // fallback, so the base is never incomplete.
  const queryClient = useQueryClient();
  const resolveEffectiveSettings = (): tLayerSettings => {
    if (localSettings) return localSettings;
    const cached = viewLayerId
      ? queryClient.getQueryData<CachedLayerSettings>(['layer-settings', viewLayerId])?.layer?.settings
      : null;
    return cached ?? { ...layerDefaultSettings(layer), ...layer?.settings };
  };

  /**
   * Give the cloud back: on close, on a switch to another run, on unmount —
   * and on MOUNT, which is the one that matters most. `layerSettingsAtom` is
   * localStorage-backed, so a reload taken mid-mode leaves the repainted
   * settings persisted; the snapshot that undoes them is persisted beside it
   * (`clearanceViewModeAtom`), and this is where it gets spent.
   *
   * Everything goes through `store` rather than the render closure so the
   * cleanup restores the LATEST snapshot instead of one captured three
   * renders ago, and `releaseViewMode` owes no write at all when the dock
   * holds nothing — so running on every mount cannot stamp on settings this
   * dock never took.
   */
  useEffect(() => {
    if (!viewLayerId) return;
    const release = () => {
      const next = releaseViewMode(store.get(clearanceViewModeAtom(viewLayerId)));
      if (!next.write) return;
      store.set(layerSettingsAtom(viewLayerId), next.write.settings);
      store.set(clearanceViewModeAtom(viewLayerId), next.state);
    };
    release();
    return release;
  }, [viewLayerId, store]);

  /**
   * Finding I1: heal a take-over that a reload left stranded with NO dock open.
   *
   * The release above only runs for the layer whose dock is open, and
   * `stationVolumeDockAtom` is not persisted — so a reload taken in Highlight
   * or Violations-only comes back with the dock closed, the repainted patch
   * still in `layerSettingsAtom` (localStorage) and the snapshot that undoes it
   * unspent in `clearanceViewModeAtom`. The user saw a magenta (or, once the
   * cloud carries classification, a nearly empty) point cloud with nothing on
   * screen saying why.
   *
   * This component is mounted unconditionally by `Viewer` — it returns null
   * with no dock, it does not unmount — so it is the level that always runs.
   * Every clearance layer in the project except the one whose dock is open is
   * swept; `releaseViewMode` owes no write at all without a hold, so a layer
   * that was never taken over is never touched.
   */
  const clearanceLayerIds = useMemo(
    () =>
      layers
        .filter((l) => !!((l.metadata ?? null) as DerivedClearanceLayerMetadata | null)?.clearance)
        .map((l) => l.id)
        .join(','),
    [layers],
  );
  useEffect(() => {
    if (!clearanceLayerIds) return;
    for (const id of clearanceLayerIds.split(',')) {
      if (id === viewLayerId) continue;
      const next = releaseViewMode(store.get(clearanceViewModeAtom(id)));
      if (!next.write) continue;
      store.set(layerSettingsAtom(id), next.write.settings);
      store.set(clearanceViewModeAtom(id), next.state);
    }
  }, [clearanceLayerIds, viewLayerId, store]);

  /**
   * Does the cloud this dock would repaint actually carry a classification
   * attribute in its octree?
   *
   * `undefined` while it is still loading, `false` for every clearance cloud
   * converted before `processClearance` started asking the converter for
   * `Classification`. On such a cloud Potree hands the shader an all-zero
   * class buffer, so Highlight paints everything context-grey and
   * Violations-only draws nothing at all — a blank viewport with no
   * explanation. Keyed by the DERIVED layer (`viewLayerId`), the cloud that
   * carries class 13, exactly like `clearanceViewModeAtom` beside it.
   *
   * The atom, the detector and `viewModeUnavailableReason` are the
   * classification seam; this dock only consumes them.
   */
  const hasClassification = usePointcloudHasClassification(viewLayerId);
  const viewModeUnavailable = viewModeUnavailableReason(hasClassification);

  const setViewMode = (next: ClearanceViewMode) => {
    if (!viewLayerId) return;
    const step = takeOverViewMode(
      viewState,
      { local: localSettings, effective: resolveEffectiveSettings() },
      next,
      hasClassification,
    );
    setViewState(step.state);
    if (step.write) setLocalSettings(step.write.settings);
  };

  /**
   * A mode the reader chose (or a reload restored) before the octree landed,
   * on a cloud that turns out to carry no classification, has to undo itself
   * the moment that is known: `clearanceViewModeAtom` is localStorage-backed,
   * so otherwise the take-over survives with no control left that could exit
   * it. Deliberately keyed on `false` only — `undefined` is "not loaded yet",
   * and re-running this on the `undefined → true` flip would throw away a mode
   * the reader had just picked.
   */
  useEffect(() => {
    if (!viewLayerId || hasClassification !== false) return;
    const state = store.get(clearanceViewModeAtom(viewLayerId));
    if (state.mode === 'scalar' && !state.hold) return;
    const next = releaseViewMode(state);
    if (next.write) store.set(layerSettingsAtom(viewLayerId), next.write.settings);
    store.set(clearanceViewModeAtom(viewLayerId), next.state);
  }, [viewLayerId, hasClassification, store]);

  /**
   * Which radio is checked. A locked dock never takes the layer over, and
   * neither does one whose cloud cannot be repainted — in both cases the
   * cloud really is on the run's own ramp, so `scalar`, not "nothing
   * selected" (finding M6).
   */
  const selectedViewMode: ClearanceViewMode = gate.allowed && hasClassification !== false ? viewState.mode : 'scalar';
  /** Populated by the buttons' ref callbacks (a commit-time write, which is
   *  what the React Compiler's no-refs-during-render rule permits) so the
   *  arrow keys can move focus, as the radiogroup pattern requires. */
  const viewModeRefs = useRef<Partial<Record<ClearanceViewMode, HTMLButtonElement | null>>>({});
  const viewModeKeys = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const delta =
      e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!delta) return;
    e.preventDefault();
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    // With no usable classification, `scalar` is the only mode there is — the
    // arrows must not walk the reader into a segment whose own click handler
    // would refuse them. The muted segments state the reason in their titles.
    if (viewModeUnavailable) return;
    const at = CLEARANCE_VIEW_MODES.indexOf(selectedViewMode);
    const next = CLEARANCE_VIEW_MODES[(at + delta + CLEARANCE_VIEW_MODES.length) % CLEARANCE_VIEW_MODES.length];
    setViewMode(next);
    viewModeRefs.current[next]?.focus();
  };

  const { data: json, isLoading, error, progress, refetch } = useClearance(meta, batchId);
  // Finding I3: `useClearance` throws a bare `SCHEMA_TOO_NEW` sentinel when the
  // byproduct was written by a newer worker than this client understands.
  // Printed raw it was an English constant in five languages, and retrying it
  // could never help — the file is not going to get older.
  const schemaTooNew = error?.message === 'SCHEMA_TOO_NEW';
  // Ruling: the CSV the worker already wrote is the export. Re-deriving one in
  // the browser would be a second implementation of the same table, free to
  // disagree with the file an engineer downloads from the batch panel.
  const csv = useClearanceByproductUrl(batchId, meta?.byproducts?.csv);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [dock?.layerId]);

  // Measure the PLOT box, not the dock: the body is padded, the header wraps
  // to a second row at narrow widths, and the resize grip decides what is left
  // over. Arithmetic on `dock.height` cannot know any of that, and when it
  // guessed high the chart overflowed and the body grew a scrollbar
  // (`StationVolumeChart` measures for exactly this reason).
  //
  // A CALLBACK REF, not `useRef` + a `useEffect` keyed on the tab. Every pane
  // is rendered conditionally on `json`, which arrives from a fetch AFTER the
  // dock mounts — so the effect ran once with `plotRef.current === null`, took
  // its early return, and (its deps unchanged by the fetch resolving) never
  // ran again. The observer was never attached at all, and every pane drew at
  // the `DEFAULT_PLOT_H` fallback for the dock's whole life. That was invisible
  // while collapsed, because 180 px is about what a 280 px dock leaves over,
  // and unmissable expanded: a 180 px chart with the rest of the screen blank
  // under it. A callback ref fires on the NODE, so it attaches the moment the
  // pane appears and re-attaches when a tab switch swaps one pane for another.
  // Expanding needs nothing extra — the observed element itself changes size.
  const plotObserverRef = useRef<ResizeObserver | null>(null);
  const plotRef = useCallback((el: HTMLDivElement | null) => {
    plotObserverRef.current?.disconnect();
    plotObserverRef.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (!r || !(r.width > 0) || !(r.height > 0)) return;
      setPlotBox((prev) => (prev && prev.w === r.width && prev.h === r.height ? prev : { w: r.width, h: r.height }));
    });
    ro.observe(el);
    plotObserverRef.current = ro;
  }, []);
  // React detaches the ref (calling it with null) when the dock unmounts, but
  // not when the whole tree is torn down mid-commit; one disconnect on unmount
  // means no observer ever outlives the element it was measuring.
  useEffect(() => () => plotObserverRef.current?.disconnect(), []);

  // Below 520 px a chart has no room; show the table rather than a chart
  // nobody can read.
  //
  // `isClearance` is part of the condition, not just of the render below: this
  // effect runs even while the component returns null, `width` is component
  // state that survives the dock closing, and the ResizeObserver stops
  // measuring while nothing is mounted. Without the guard, opening this dock in
  // a narrow window and later opening the VOLUMES dock in a wide one would
  // retab the volumes dock from a stale measurement (M8).
  useEffect(() => {
    if (!dock || !isClearance) return;
    if (width < DOCK_VERY_NARROW_PX && dock.tab === 'chart') setDock({ ...dock, tab: 'table' });
  }, [width, dock, isClearance, setDock]);

  // Drop the stale measurement when the dock closes, so the next open starts
  // from the observer's first real frame rather than from the last window size
  // this component happened to see.
  useEffect(() => {
    if (!isClearance) setWidth(1200);
  }, [isClearance]);

  const bins: ClearanceBin[] = useMemo(() => json?.bins ?? [], [json]);
  const sel = selection && dock && selection.layerId === dock.layerId ? selection : null;

  const scoped = useMemo(() => (sel ? bins.filter((b) => b.i >= sel.fromBin && b.i <= sel.toBin) : bins), [bins, sel]);
  const totals = useMemo(() => {
    const violations = scoped.reduce((acc, b) => acc + b.violationCount, 0);
    const worstBin = scoped.reduce<ClearanceBin | null>(
      (acc, b) =>
        b.maxIntrusionM !== null && (acc === null || b.maxIntrusionM > (acc.maxIntrusionM ?? -Infinity)) ? b : acc,
      null,
    );
    // The worker recorded the EXACT station of the worst point, but only once,
    // for the run as a whole. Use it whenever the worst bin in view is that
    // same bin — quoting `1+030` (the bin midpoint) for a point the JSON says
    // sits at `1+031` would be the dock inventing a figure it was handed. Under
    // a brush that excludes the run's worst bin there is no per-bin equivalent
    // in the schema, so the midpoint is the best number available — and it is
    // marked `~` and given the bin's exact range as its title, because a figure
    // that can be half a bin out must not be printed as if it were exact.
    const summaryWorst = json?.summary;
    const exact =
      !!worstBin &&
      !!summaryWorst &&
      summaryWorst.worstBinIndex === worstBin.i &&
      summaryWorst.worstStationDisplayM !== null;
    const worstStationDisplayM = !worstBin
      ? null
      : exact
        ? summaryWorst!.worstStationDisplayM!
        : (worstBin.staStartDisplayM + worstBin.staEndDisplayM) / 2;
    return {
      violations,
      worst: worstBin?.maxIntrusionM ?? null,
      worstStationDisplayM,
      worstStationExact: exact,
      worstStationRange: worstBin ? formatStationRange(worstBin, 2) : null,
      binsWithViolations: scoped.filter((b) => b.violationCount > 0).length,
    };
  }, [scoped, json]);

  const selectedBin = sel ? (bins.find((b) => b.i === sel.fromBin) ?? null) : null;

  // Finding I3, mirroring `StationVolumeDock`: every station this dock prints
  // was computed against the alignment as it stood when the run went out. If
  // that alignment has been edited since, the figures are still correct about
  // the cloud and wrong about WHERE — which is the failure a reader cannot see.
  // An unregistered alignment matches by definition (`alignmentHashMatches`),
  // so a project that has not loaded the LandXML yet gets no false alarm.
  const liveAlignment: Alignment | undefined = meta ? alignments.get(meta.alignment.key) : undefined;
  const alignmentStale = meta ? !alignmentHashMatches(meta.alignment, liveAlignment) : false;

  // ---- Opening the dock starts a walk (Task 6a) ---------------------------
  //
  // That is what makes the chart cursor, the 3D plates and the envelope one
  // instrument rather than three — and it is what makes `[` / `]` work, since
  // `AlignmentWalkToolbar` keys off `stationDockOpenAtom`, which does not care
  // which dataset is open. `analysis.clearance` and `viewer.alignmentWalk` are
  // the same tier, so this is not a way around the walk gate; on a lower tier
  // the dock simply never starts a walk.
  const isClearanceDock = isClearance && !!dock;
  useEffect(() => {
    if (!gate.allowed || !isClearanceDock || !liveAlignment || !json) return;
    setWalk((w) =>
      w?.key === liveAlignment.key
        ? w
        : {
            key: liveAlignment.key,
            station: json.params.stationFromInternalM,
            step: json.params.stationIntervalM || DEFAULT_WALK_STEP,
            snapKeyPoints: false,
            follow: false,
          },
    );
  }, [gate.allowed, isClearanceDock, liveAlignment, json, setWalk]);

  // ---- "Clip to station" (Task 6d) ---------------------------------------
  //
  // A line-for-line port of `StationVolumeDock`'s own take-over, with one
  // difference that is forced on it: the slab's dimensions come from the
  // PROFILE's bounding box (`clearanceClipDims`), because a clearance run
  // writes no `station_offsets.f32` planes for `clipDimsForRun` to read
  // (ruling R1).
  const walkActive = !!walk && !!meta && walk.key === meta.alignment.key;
  const clipAllowed = gate.allowed && clipGate.allowed;
  // Recomputed as the walk steps, and deliberately: `verticalCentre` is an
  // ABSOLUTE elevation (ruling P7), so on a graded alignment the slab has to
  // rise with the station or it slides out of the cloud a few hundred metres
  // along. The station is the only moving part — the memo is stable between
  // steps, so the effect below does not re-fire per frame.
  const walkStation = walk?.station;

  /**
   * Which station the cross-section stands at. The WALK wins when it is on
   * this alignment — that is what makes stepping through the corridor and
   * watching the section change feel like one instrument rather than two —
   * and the brush is the fallback so a reader who clicked a bar on the Chart
   * tab finds that bin already drawn when they switch to Profile.
   *
   * Array POSITION, not `bin.i`: the section grid's station axis is indexed by
   * position in `bins[]` (the contract the writer and this reader share), and
   * the two coincide today only because the worker emits every bin in order.
   */
  const sectionBinIndex = useMemo(() => {
    if (walkStation != null && walkActive) {
      const i = bins.findIndex((b) => walkStation >= b.staStartInternalM && walkStation < b.staEndInternalM);
      if (i >= 0) return i;
    }
    if (selectedBin) {
      const i = bins.indexOf(selectedBin);
      if (i >= 0) return i;
    }
    return 0;
  }, [bins, selectedBin, walkActive, walkStation]);

  /** Step the section, taking the brush and (when live) the walk with it. */
  const dockLayerId = dock?.layerId;
  const stepSectionBin = useCallback(
    (next: number) => {
      const b = bins[Math.max(0, Math.min(bins.length - 1, next))];
      if (!b || !json || !dockLayerId) return;
      setSelection({ layerId: dockLayerId, fromBin: b.i, toBin: b.i });
      const centre = (b.staStartInternalM + b.staEndInternalM) / 2;
      setWalk((w) => (w && w.key === json.alignment.key ? { ...w, station: centre } : w));
    },
    [bins, dockLayerId, json, setSelection, setWalk],
  );
  const clipDims = useMemo(
    () =>
      meta && liveAlignment && walkStation !== undefined
        ? clearanceClipDims(meta.params, liveAlignment, walkStation)
        : null,
    [meta, liveAlignment, walkStation],
  );

  /**
   * What the walk's clip was before we took it over, and what the viewer's
   * clip box looked like. Refs, not state: they are written from an effect and
   * never rendered, and React Compiler forbids reading a ref during render.
   *
   * `undefined` on the walk ref means "we have not taken it over"; `null` is a
   * real previous value (no slice), which is why the two cannot share a
   * sentinel.
   */
  const clipRestoreRef = useRef<ClipDims | null | undefined>(undefined);
  // GEOMETRY is part of "as we found it", not just the flags. Restoring
  // `enabled`/`clipMode` alone handed a user who already had a clip box of
  // their own back a box that was still enabled and still sitting on the
  // station slab this dock had moved it to — which reads, correctly, as
  // "I turned Clip to station off and the clipping did not turn off".
  const boxRestoreRef = useRef<Pick<
    ClipBoxState,
    'enabled' | 'interactionMode' | 'clipMode' | 'center' | 'halfSize'
  > | null>(null);

  /** Give the walk and the clip box back exactly as we found them. */
  const releaseClip = useCallback(() => {
    if (clipRestoreRef.current !== undefined) {
      const previous = clipRestoreRef.current;
      clipRestoreRef.current = undefined;
      setWalk((w) => (w ? { ...w, clip: previous } : w));
    }
    const box = boxRestoreRef.current;
    if (box) {
      boxRestoreRef.current = null;
      store.set(clipBoxStateAtom(projectId), (prev) => ({ ...prev, ...box }));
    }
  }, [projectId, setWalk, store]);

  useEffect(() => {
    if (!clipToBin || !clipDims || !meta || !walkActive || !liveAlignment || !clipAllowed) {
      // Restore exactly once, and only if we were the ones who changed it.
      releaseClip();
      return;
    }
    const current = store.get(alignmentWalkAtom);
    if (!current) return;
    // The slab belongs to a BIN, not to wherever the slider happens to sit, so
    // the station is snapped ON THE WAY ON and never again: re-snapping on
    // every `clipDims` identity change would yank a mid-bin walk out from
    // under the user for no reason they could see.
    const takingOver = clipRestoreRef.current === undefined;
    if (takingOver) clipRestoreRef.current = current.clip ?? null;
    const i = binIndexForStation(meta.params, bins.length, current.station);
    const station = takingOver && bins[i] ? binCentreStation(bins[i]) : current.station;
    setWalk({ ...current, station, clip: clipDims });
    // Enable the box in the SAME batch as the walk write, exactly as the walk
    // toolbar's own clip-follow does: that toolbar cancels a `walk.clip` it
    // sees alongside a switched-off box (it reads that as "the user turned the
    // box off elsewhere"), so writing the walk alone would be undone before
    // the next frame.
    const box = store.get(clipBoxStateAtom(projectId));
    if (!boxRestoreRef.current) {
      boxRestoreRef.current = {
        enabled: box.enabled,
        interactionMode: box.interactionMode,
        clipMode: box.clipMode,
        center: box.center,
        halfSize: box.halfSize,
      };
    }
    store.set(clipBoxStateAtom(projectId), {
      ...box,
      ...clipBoxAtStation(liveAlignment, station, clipDims),
      enabled: true,
      interactionMode: 'VIEW',
      clipMode: 'CLIP_OUTSIDE',
    });
  }, [clipToBin, clipDims, meta, walkActive, liveAlignment, clipAllowed, bins, projectId, releaseClip, setWalk, store]);

  /**
   * `walk.clip` has two owners — this toggle and the walk toolbar's own ⬚
   * button, which also nulls it when the clip box is switched off anywhere
   * else. Nothing tells us when the other one acts, and none of the effect
   * above's dependencies move when it does, so the checkbox would sit there
   * ticked over a slab that has stopped following the station.
   *
   * Follow the real state, one way: the slab is gone, so stop claiming it. The
   * walk-clip restore is dropped with it — the other owner has already decided
   * what `walk.clip` should be — but `boxRestoreRef` is deliberately kept, so
   * the release below still hands the viewer's own clip box back.
   */
  const walkClipPresent = !!walk?.clip;
  useEffect(() => {
    if (!clipToBin || !walkActive) return;
    if (clipRestoreRef.current === undefined) return;
    // The LIVE value, not `walkClipPresent`: on the commit that ticks the box
    // both effects run against a render where `walk.clip` was still null, and
    // the render-time snapshot would read the take-over as an external clear
    // and undo it. `walkClipPresent` stays in the dependency list as the
    // trigger — it is what changes when another owner acts.
    if (store.get(alignmentWalkAtom)?.clip) return;
    clipRestoreRef.current = undefined;
    setClipToBin(false);
  }, [clipToBin, walkActive, walkClipPresent, setClipToBin, store]);

  // Closing the dock is not "keep my cloud sliced": the toggle is a dock
  // control, so the take-over ends with the dock. `releaseClip` is stable, so
  // this cleanup only ever runs on unmount.
  useEffect(() => releaseClip, [releaseClip]);

  /**
   * The mirror of `StationVolumeDock`'s own stale-latch reset (its finding
   * F-24): a volumes chip REPLACES the shared `dock` atom rather than calling
   * this dock's close handler, so the reset in that handler never runs and
   * `stationClipToBinAtom` for the clearance layer stays latched `true` on its
   * own atomFamily entry. Track the layerId THIS dock last owned — independent
   * of `dock` moving on to another dataset — so the reset lands on that entry.
   */
  const ownLayerIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isClearanceDock && dock) ownLayerIdRef.current = dock.layerId;
  }, [isClearanceDock, dock]);
  useEffect(() => {
    if (isClearanceDock) return;
    const staleLayerId = ownLayerIdRef.current;
    if (!staleLayerId) return;
    ownLayerIdRef.current = null;
    store.set(stationClipToBinAtom(staleLayerId), false);
  }, [isClearanceDock, store]);

  /** Where "Go to worst" goes, or null on a clean run (`clearanceWorstTarget`). */
  const worstTarget = useMemo(() => (json ? clearanceWorstTarget(json) : null), [json]);

  const startResize = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dock) return;
    const startY = e.clientY;
    const startH = dock.height;
    const base = { layerId: dock.layerId, tab: dock.tab, dataset: dock.dataset };
    const move = (ev: globalThis.PointerEvent) =>
      setDock({ ...base, height: clampHeight(startH + (startY - ev.clientY)) });
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  const resizeKeys = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!dock) return;
    const delta = e.key === 'ArrowUp' ? RESIZE_KEY_STEP : e.key === 'ArrowDown' ? -RESIZE_KEY_STEP : 0;
    if (!delta) return;
    e.preventDefault();
    setDock({ ...dock, height: clampHeight(dock.height + delta) });
  };

  if (!dock || !isClearance || !meta) return null;

  const p = meta.params;
  /** What the loading/error badge is ABOUT. The derived layer's own name when
   *  the layers query has it, otherwise the alignment the run swept — the same
   *  fallback the sibling dock's badge uses. */
  const badgeName = layer?.name ?? meta.alignment.name;
  const bands = (json?.params.nearMissBandsM ?? p.nearMissBandsM ?? []).map((b) => formatFixed(b, 2)).join(' / ');
  // Finding F-6: every station this dock prints — the KPI row, the brush line,
  // the chart/table axes, and the worker's own CSV — is a DISPLAY station. The
  // header's range must be one too, or "0.00 – 1200.00" sits next to "worst at
  // 12+430" with nothing telling the reader they are two different coordinate
  // systems. Prefer the bins' own display stations (matches the CSV exactly);
  // fall back to converting the run's INTERNAL params through the alignment
  // when bins have not loaded yet — the same `rangeText` shape
  // `StationVolumeDock` builds, with this clearance view retaining its 2 dp.
  const rangeText = formatStationRange(
    {
      staStartDisplayM: bins.length
        ? bins[0].staStartDisplayM
        : internalToDisplayRaw(p.stationFromInternalM, meta.alignment.staStart, meta.alignment.equations),
      staEndDisplayM: bins.length
        ? bins[bins.length - 1].staEndDisplayM
        : internalToDisplayRaw(p.stationToInternalM, meta.alignment.staStart, meta.alignment.equations),
    },
    2,
  );

  return (
    <div
      ref={rootRef}
      data-testid='clearance-dock'
      role='region'
      aria-label={t('clearance.dock.title')}
      // `sv-root` is what SV_TOKENS scopes its custom properties to
      // (`tokens.ts` targets `.sv-root,.sv-palette`). Without the class the
      // injected <style> is inert and every `var(--sv-*)` below silently
      // falls through to its literal fallback.
      className={`sv-root pointer-events-auto flex w-full flex-col border-t border-slate-200 bg-white dark:border-stone-700 dark:bg-stone-900 ${
        expanded ? 'absolute inset-0 z-30 h-auto' : 'relative'
      }`}
      // Expanded, the height is the parent's, not the grip's — the
      // absolute inset-0 above already fixes all four edges.
      style={expanded ? undefined : { height: dock.height }}
    >
      <style>{SV_TOKENS}</style>
      {/* Injected once per overlay container, as `LoadingBadge`'s contract
          requires — the indeterminate bar's animation lives in it. */}
      <LoadingBadgeKeyframes />

      {/* 8 px of visible grip inside a 24 px hit target, the same treatment the
          station dock uses (spec §5 A11y point 5). Both docks share one
          persisted height, so a dock that could not change it was an asymmetry
          the user had to work around by opening the other one (M5). */}
      {/* No grip while expanded: there is nothing left to resize, and the
          grip deliberately straddles the dock's TOP edge — which, expanded,
          is the top of the viewer region, under the app's own chrome. */}
      <div className={`relative w-full shrink-0 ${expanded ? 'h-0' : 'h-2'}`}>
        {!expanded && (
          <>
            <div
              data-testid='clearance-resize'
              role='separator'
              aria-orientation='horizontal'
              aria-label={t('clearance.dock.resize')}
              aria-valuenow={dock.height}
              aria-valuemin={DOCK_HEIGHT_MIN}
              aria-valuemax={DOCK_HEIGHT_MAX}
              title={t('clearance.dock.resizeHelp')}
              tabIndex={0}
              onPointerDown={startResize}
              onKeyDown={resizeKeys}
              className='absolute inset-x-0 -top-2 h-6 cursor-ns-resize focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none'
            />
            <div aria-hidden className='h-full w-full bg-slate-200/70 dark:bg-stone-700/70' />
          </>
        )}
      </div>

      {/* Corner close, the treatment `StationVolumeDock` and every other
          viewer panel uses. It used to sit inline at the end of the control
          row, where it read as one more action on the data rather than as
          "put this panel away" — and it was the control the row squeezed off
          the edge first. 24 px hit target, above the header's own padding. */}
      {/* Fullscreen. Next to the close button because the two are the same
          kind of control — what the PANEL does, not what the data does — and
          because the corner is where every other viewer panel puts them. The
          expanded dock covers the canvas and the layers sidebar but stops at
          the app's own chrome: it is `absolute inset-0` inside the viewer
          root, which is itself inset below the top bar and right of the left
          sidebar, so there is no offset arithmetic to keep in step with a
          sidebar someone later resizes.

          `top-4` while COLLAPSED, `top-2` while expanded: the collapsed dock
          gives its top 8 px to the resize grip, so a button at `top-2` sits
          flush against it with no breathing room at all. There is no grip
          while expanded, so `top-2` is right there. Matches
          `StationVolumeDock`. */}
      <button
        data-testid='clearance-expand'
        type='button'
        aria-pressed={expanded}
        aria-label={t(expanded ? 'stationVolumes.panel.collapse' : 'stationVolumes.panel.expand')}
        title={t(expanded ? 'stationVolumes.panel.collapse' : 'stationVolumes.panel.expand')}
        onClick={() => setExpanded(!expanded)}
        className={`absolute right-8 z-10 flex size-6 items-center justify-center rounded text-slate-500 hover:bg-slate-200/70 hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:text-stone-400 dark:hover:bg-stone-700/70 dark:hover:text-white ${expanded ? 'top-2' : 'top-4'}`}
      >
        {expanded ? <Minimize2 className='size-4' /> : <Maximize2 className='size-4' />}
      </button>
      <button
        data-testid='clearance-close'
        type='button'
        aria-label={t('clearance.dock.close')}
        title={t('clearance.dock.close')}
        onClick={() => {
          setDock(null);
          setSelection(null);
          // The slab takes over the viewer's clip box — shared state — so
          // reopening the dock has to ask again rather than silently
          // re-slicing the cloud (ruling D36). The envelope stays sticky
          // because it touches nothing shared.
          setClipToBin(false);
        }}
        className={`absolute right-2 z-10 flex size-6 items-center justify-center rounded text-slate-500 hover:bg-slate-200/70 hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:text-stone-400 dark:hover:bg-stone-700/70 dark:hover:text-white ${expanded ? 'top-2' : 'top-4'}`}
      >
        <X className='size-4' />
      </button>

      {/* ---- Header: what was checked, over what range, finding what ----
              TWO dense rows: identity on the left of the figures, then the
              measurement chips on the left of the controls that act on them.
              Nothing gets a row of its own — the long attribution used to
              force the KPI block to wrap under an empty line. Everything here
              is `shrink-0`; the tab body is the only row that gives. */}
      <div className='flex shrink-0 flex-col gap-y-1.5 border-b border-slate-200 px-4 py-2 dark:border-stone-700'>
        <div className='flex flex-wrap items-start justify-between gap-x-6 gap-y-1.5'>
          {/* 1 — Title: the eyebrow says which instrument, the name says which
              run, and the range says over what. */}
          <div className='flex min-w-0 flex-wrap items-baseline gap-x-2'>
            <span className='text-[10px] tracking-wide text-slate-500 uppercase dark:text-stone-400'>
              {t('clearance.dock.eyebrow')}
            </span>
            {/* Deliberately NOT truncated (and the row wraps instead): the
                profile name is the identity of the run, and at the old width
                the header clipped it to "Clea…" — a title that names nothing. */}
            <span data-testid='clearance-dock-profile' className='text-sm font-semibold'>
              {meta.profile.name}
            </span>
            {/* NOT `truncate`. `overflow: hidden` makes an inline element
                establish its own formatting context, and its baseline then
                becomes its bottom margin edge rather than its text baseline —
                so inside this `items-baseline` row it sat visibly lower than
                the eyebrow, the profile name and the station range beside it.
                The row wraps instead; a long alignment name costs a second
                line, not a misaligned one. */}
            <span data-testid='clearance-dock-scope' className='text-slate-500 dark:text-stone-400'>
              {t('clearance.dock.scopeAlong', {
                alignment: meta.alignment.name,
                offset: formatFixed(p.verticalOffsetM, 2),
                // React escapes at render; i18next escaping on top of it turns
                // "/" and any "&" or apostrophe in an alignment name into
                // literal HTML entities on screen.
                interpolation: { escapeValue: false },
              })}
            </span>
            {/* The sibling dock's own label, like the offset-convention chip
                below: one range in one monospace face, called one thing. */}
            <span className='sv-sta text-slate-600 dark:text-stone-300' title={t('stationVolumes.panel.rangeLabel')}>
              <span data-testid='clearance-range'>{rangeText}</span>
            </span>
          </div>
          {/* `pr-16` keeps this row clear of the corner buttons — there are TWO
              of them now (fullscreen and close), and the old `pr-8` reserved
              room for one, so "Go to worst" collided with the fullscreen button
              at wide widths. Same figure, same reason, as `StationVolumeDock`. */}
          <div className='flex flex-wrap items-start justify-end gap-x-6 gap-y-1.5 pr-16'>
            <DockKpi
              hero
              label={t('clearance.kpi.violations')}
              value={totals.violations.toLocaleString()}
              testId='clearance-kpi-violations'
            />
            <DockKpi
              hero
              label={t('clearance.kpi.worst')}
              value={totals.worst === null ? '—' : formatFixed(totals.worst, 3)}
              unit='m'
              unitTestId='clearance-kpi-worst-unit'
              testId='clearance-kpi-worst'
            />
            {/* No leading `~` any more: a tilde in front of a station reads as
                part of the figure, and it was the one KPI that printed a
                punctuation mark the CSV, the chart and the table never use.
                The caveat itself is not dropped — an inexact figure still
                carries the bin's real span in its tooltip. */}
            <DockKpi
              mono
              label={t('clearance.kpi.worstStation')}
              value={totals.worstStationDisplayM === null ? '—' : staTag(totals.worstStationDisplayM, 0)}
              title={
                totals.worstStationDisplayM === null || totals.worstStationExact
                  ? undefined
                  : t('clearance.kpi.worstStationApprox', { range: totals.worstStationRange ?? '' })
              }
              testId='clearance-kpi-worst-station'
            />
            <DockKpi
              label={t('clearance.kpi.bins')}
              value={totals.binsWithViolations.toLocaleString()}
              testId='clearance-kpi-bins'
            />
            {/* The station above is a figure; this is the way to it, so it
                stays with the figures rather than migrating into the control
                cluster. `disabled` is right here and only here — it is a DATA
                condition (a clean run has no worst point), not a paywall, so
                there is no click that needs to reach an upgrade dialog. */}
            <Button
              data-testid='clearance-jump-worst'
              type='button'
              size='sm'
              variant='outline'
              className='h-7 self-center px-2'
              disabled={!worstTarget}
              title={t('clearance.dock.jumpWorstHelp')}
              onClick={() => {
                if (!worstTarget || !json) return;
                setSelection({ layerId: dock.layerId, fromBin: worstTarget.bin.i, toBin: worstTarget.bin.i });
                // The worker recorded the EXACT station of the worst point, so
                // the walk lands ON the point rather than near it.
                setWalk((w) => (w && w.key === json.alignment.key ? { ...w, station: worstTarget.station } : w));
              }}
            >
              {t('clearance.dock.jumpWorst')}
            </Button>
          </div>
        </div>

        {/* Row 2 — how it was measured (chips) on the left of the
            controls that act on the figures above. Wraps rather than
            overflows: at 760 px the controls drop under the chips
            instead of pushing them off the edge. */}
        <div className='flex flex-wrap items-start justify-between gap-x-6 gap-y-1.5'>
          {/* 2 — How it was measured, as chips. This row is why the dock no
              longer has a footer: a figure without the settings that produced
              it cannot be checked, reproduced or argued with (M9), and those
              settings belong beside the name of the run they parameterise
              rather than in a strip under the chart, where the reader had to
              look away from the KPIs to find them. */}
          <div className='flex flex-wrap items-center gap-1.5'>
            {/* The disclosure, shared with the sibling dock's flag so the two
                behave identically. Parameters are reference material — read
                once, then in the way on every later glance — so they fold
                away, but they stay ONE CLICK from the figures they qualify
                rather than being deleted (M9). */}
            <button
              type='button'
              data-testid='clearance-params-toggle'
              aria-expanded={paramsOn}
              title={t(paramsOn ? 'stationVolumes.panel.hideParams' : 'stationVolumes.panel.showParams')}
              onClick={() => setParamsOn(!paramsOn)}
              className='sv-chip cursor-pointer text-[10px] text-slate-500 hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:text-stone-400 dark:hover:text-white'>
              {t(paramsOn ? 'stationVolumes.panel.hideParams' : 'stationVolumes.panel.showParams')}
            </button>
            {paramsOn && (
              <>
            <DockChip testId='clearance-chip-method' title={t('clearance.dock.chipMethodHelp')}>
              <span className='sv-num'>
                {t('clearance.dock.chipMethod', {
                  // B-2: intervals may be fractional (1–500, any finite) — 2 dp with
                  // trailing zeros trimmed, so 20 prints "20" and 12.5 prints "12.5".
                  interval: String(Math.round(p.stationIntervalM * 100) / 100),
                  violationClass: p.violationClass,
                })}
              </span>
            </DockChip>
            <DockChip testId='clearance-chip-bands' title={t('clearance.dock.chipBandsHelp')}>
              <span className='sv-num'>
                {t('clearance.dock.chipBands', { bands: bands || '—', interpolation: { escapeValue: false } })}
              </span>
            </DockChip>
            {/* The sibling dock's sentence, from the sibling dock's own key:
                both docks print signed offsets in the same station frame, so
                they must state the convention in the same words or a reader
                moving between them has to work out whether they agree. */}
            <DockChip testId='clearance-chip-offset'>{t('stationVolumes.panel.offsetConvention')}</DockChip>
            {/* Ruling K4: the standard the dimensions came from travels with
                the run, so the result can always be traced back to it — even
                after the shipped preset list changes or the saved profile is
                deleted. Truncated with the full text in the tooltip, so a long
                citation cannot push the figures off the header's first row. */}
            {meta.profile.sourceAttribution && (
              <span
                data-testid='clearance-dock-attribution'
                className='min-w-0 max-w-[30rem] truncate text-[11px] text-slate-400 dark:text-stone-500'
                title={meta.profile.sourceAttribution}
              >
                {meta.profile.sourceAttribution}
              </span>
            )}
              </>
            )}
            {/* A WARNING, never folded away: "the alignment has changed since
                this run" is not a parameter, it is a reason to distrust every
                number beside it. */}
            {alignmentStale && (
              <DockChip tone='warn' testId='clearance-stale-alignment' title={t('clearance.dock.alignmentChangedHelp')}>
                {t('clearance.dock.alignmentChanged')}
              </DockChip>
            )}
          </div>
          <div className='flex flex-wrap items-center justify-end gap-x-2 gap-y-1.5'>
            {/* ONE 3D switch, not two. The envelope now carries the per-bin
                verdict, the brush selection and the hover that the flat
                corridor plates used to, so the plates' own checkbox went with
                them — two toggles for what the reader sees as one object was
                a question that had no good answer.

                Gated, but never `disabled`: the HTML attribute would eat the
                click that is the only route to the upgrade dialog (CLAUDE.md).
                Locked it renders muted and unchecked, stays in the tab order,
                and answers a click with the paywall.

                Its key is NOT here. The envelope's bands are painted from
                `CLEARANCE_FILL`, which is the same map the chart's bars are
                painted from, so the ladder was keyed twice in one dock — once
                over the chart and once stacked in this corner. One key, in the
                chart header, exactly where `StationVolumeChart` puts its own
                cut/fill key. */}
            <label
              className={`flex items-center gap-1.5 text-xs ${gate.allowed ? '' : 'opacity-60'}`}
              title={t('clearance.panel.envelopeHelp')}
            >
              <Checkbox
                data-testid='clearance-envelope'
                checked={gate.allowed && envelopeOn}
                aria-disabled={gate.allowed ? undefined : true}
                data-locked={gate.allowed ? undefined : 'true'}
                onCheckedChange={(c) => {
                  if (!gate.allowed) {
                    gate.promptUpgrade();
                    return;
                  }
                  setEnvelopeOn(!!c);
                }}
              />
              {t('clearance.panel.envelope')}
              {!gate.allowed && <UpgradeBadge gate={gate.gate} />}
            </label>
            {/* Two reasons this control can be unavailable, and they are
                answered differently: no walk is a DATA condition (inert, with
                the reason in the title), a locked tier is a PAYWALL (still
                clickable — the HTML `disabled` attribute would eat the only
                click that reaches the upgrade dialog). */}
            <label
              className={`flex items-center gap-1.5 text-xs ${clipAllowed && walkActive ? '' : 'opacity-60'}`}
              // A locked control is muted for a reason the badge states, not
              // for the missing walk — so the hint only speaks when the walk is
              // the thing that is missing.
              title={clipAllowed && !walkActive ? t('clearance.dock.walkHint') : t('clearance.dock.clipToBinHelp')}
            >
              <Checkbox
                data-testid='clearance-clip-to-bin'
                checked={clipAllowed && clipToBin}
                aria-disabled={!walkActive || !clipAllowed ? true : undefined}
                data-locked={clipAllowed ? undefined : 'true'}
                onCheckedChange={(c) => {
                  if (!gate.allowed) {
                    gate.promptUpgrade();
                    return;
                  }
                  if (!clipGate.allowed) {
                    clipGate.promptUpgrade();
                    return;
                  }
                  if (!walkActive) return;
                  setClipToBin(!!c);
                }}
              />
              {t('clearance.dock.clipToBin')}
              {/* Whichever half is locked is the one to name: a user with the
                  clearance analysis but no clip box would otherwise see a muted
                  control with no plan on it and meet the paywall on click. */}
              {!clipAllowed && <UpgradeBadge gate={(gate.allowed ? clipGate : gate).gate} />}
            </label>
            {/* A radiogroup, not a third checkbox: a cloud is painted one way
                at a time, and the three modes are exclusive. Same gated
                treatment as the toggles above — muted and still clickable,
                never the HTML `disabled` attribute that would swallow the
                click to the upgrade dialog. */}
            <div
              role='radiogroup'
              data-testid='clearance-view-mode'
              aria-label={t('clearance.panel.viewMode')}
              title={t('clearance.panel.viewModeHelp')}
              onKeyDown={viewModeKeys}
              className={`flex items-center gap-0.5 rounded border border-slate-200 p-0.5 text-xs dark:border-stone-700 ${
                gate.allowed ? '' : 'opacity-60'
              }`}
            >
              {CLEARANCE_VIEW_MODES.map((mode) => {
                // Finding M6, second half: `gate.allowed && …` left a locked
                // radiogroup with NO radio checked — an invalid group, and a
                // reader with no way to tell what the cloud is doing. A locked
                // dock never takes the layer over, so what is on screen IS the
                // run's default; say so.
                const active = selectedViewMode === mode;
                // `scalar` is always available — it is the run's own ramp, and
                // needs no classification attribute to draw. The other two
                // colour and filter BY class 13, so they go muted (never the
                // HTML `disabled` attribute, which would take the tooltip's
                // only carrier out of the accessibility tree) with the reason
                // in their title.
                const unavailable = mode !== 'scalar' && viewModeUnavailable !== null;
                return (
                  <button
                    key={mode}
                    ref={(el) => {
                      viewModeRefs.current[mode] = el;
                    }}
                    type='button'
                    role='radio'
                    data-testid={`clearance-view-mode-${mode}`}
                    aria-checked={active}
                    aria-disabled={!gate.allowed || unavailable ? true : undefined}
                    data-locked={gate.allowed ? undefined : 'true'}
                    data-unavailable={unavailable ? viewModeUnavailable : undefined}
                    // Finding M6, first half: the ARIA radiogroup pattern is
                    // ONE tab stop with the arrows moving inside it. Three
                    // natively-focusable buttons cost a keyboard reader three,
                    // in a header row that already has five controls.
                    tabIndex={active ? 0 : -1}
                    title={
                      unavailable && viewModeUnavailable
                        ? t(VIEW_MODE_UNAVAILABLE_KEYS[viewModeUnavailable])
                        : t(VIEW_MODE_KEYS[mode].help)
                    }
                    className={`rounded px-1.5 py-0.5 ${unavailable ? 'opacity-40' : ''} ${
                      active ? 'bg-slate-200 font-semibold dark:bg-stone-700' : 'text-slate-600 dark:text-stone-300'
                    }`}
                    onClick={() => {
                      // The paywall answers first: it is the one "disabled"
                      // state with somewhere for the click to go.
                      if (!gate.allowed) {
                        gate.promptUpgrade();
                        return;
                      }
                      if (unavailable) return;
                      setViewMode(mode);
                    }}
                  >
                    {t(VIEW_MODE_KEYS[mode].label)}
                  </button>
                );
              })}
              {!gate.allowed && <UpgradeBadge gate={gate.gate} />}
            </div>
            {/* One Export menu, the sibling dock's own shape, rather than a
                bare link loose among the checkboxes — which is where it read
                as a fourth toggle. The CSV the worker already wrote IS the
                export (ruling): re-deriving one in the browser would be a
                second implementation of the same table, free to disagree with
                the file an engineer downloads from the batch panel. */}
            {meta.byproducts.csv && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid='clearance-export' type='button' size='sm' variant='outline' className='h-7'>
                    {t('clearance.dock.exportMenu')}
                    <ChevronDown className='ml-1 size-3.5' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  {csv.url ? (
                    <DropdownMenuItem asChild>
                      <a
                        data-testid='clearance-download-csv'
                        href={csv.url}
                        download={meta.byproducts.csv}
                        title={t('clearance.dock.downloadCsvTitle')}
                      >
                        {t('clearance.dock.downloadCsv')}
                      </a>
                    </DropdownMenuItem>
                  ) : (
                    // The filename is registered but its signed URL is not in
                    // hand yet (or the batch query failed). Say which, rather
                    // than silently dropping the only export the dock offers.
                    <DropdownMenuItem
                      data-testid='clearance-download-csv-pending'
                      disabled
                      className='flex-col items-start gap-0.5'
                    >
                      <span>{t('clearance.dock.downloadCsv')}</span>
                      <span className='text-[11px] text-slate-500 dark:text-stone-400'>
                        {csv.error ? csv.error.message : t('clearance.dock.downloadCsvPending')}
                      </span>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      <div className='flex items-center justify-between gap-4 border-b border-slate-200 px-4 dark:border-stone-700'>
        <div role='tablist' aria-label={t('clearance.dock.title')} className='flex gap-1'>
          {TABS.map((tab) => (
            <button
              key={tab}
              type='button'
              role='tab'
              id={`clearance-tab-${tab}`}
              aria-controls='clearance-tabpanel'
              data-testid={`clearance-tab-${tab}`}
              aria-selected={dock.tab === tab}
              className={`px-2 py-1 text-xs ${dock.tab === tab ? 'border-b-2 border-sky-500 font-semibold' : ''}`}
              onClick={() => setDock({ ...dock, tab })}
            >
              {t(`clearance.dock.tab.${tab}`)}
            </button>
          ))}
        </div>
        {/* Every KPI above silently narrows to the brush, so the brush has to
            be visible and reversible from the same row (M4). */}
        <div className='flex min-w-0 items-center gap-2 text-xs text-slate-500 dark:text-stone-400'>
          <span data-testid='clearance-selection' className='truncate'>
            {sel
              ? t('clearance.dock.selection', {
                  count: scoped.length,
                  range: scoped.length
                    ? formatStationRange(
                        {
                          staStartDisplayM: scoped[0].staStartDisplayM,
                          staEndDisplayM: scoped[scoped.length - 1].staEndDisplayM,
                        },
                        2,
                      )
                    : '',
                })
              : t('clearance.dock.selectionNone')}
          </span>
          {sel && (
            <Button
              data-testid='clearance-clear-selection'
              type='button'
              size='sm'
              variant='ghost'
              className='h-6 px-2'
              onClick={() => setSelection(null)}
            >
              {t('clearance.dock.clearSelection')}
            </Button>
          )}
        </div>
      </div>

      {/* `overflow-hidden`, not `auto` — the sibling dock's rule. The chart is
          sized from the MEASURED plot box below, so it always fits; a scrollbar
          here meant the chart had been handed a height the body did not have
          (it was guessed as `dock.height - 130`, which stopped being true the
          moment the header wrapped to a second row). Content that genuinely
          does not fit — the table, the profile pane — scrolls in its OWN
          container, so the chart can never be pushed out of view. */}
      <div
        id='clearance-tabpanel'
        role='tabpanel'
        aria-labelledby={`clearance-tab-${dock.tab}`}
        className='flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-2'
      >
        {/* No swallowed errors: a failed byproduct fetch says what failed and
            offers the retry, rather than showing an empty dock. The house
            `LoadingBadge` renders it (CLAUDE.md: do not reimplement this UI
            inline) — the headline above it is what names the thing that
            failed, exactly as `StationVolumeDock` does it. */}
        {error && (
          <div data-testid='clearance-load-error' className='flex flex-col items-start gap-2'>
            <p className='text-sm font-semibold'>{t('clearance.dock.failed')}</p>
            <LoadingBadge
              name={badgeName}
              phase='failed'
              error={schemaTooNew ? t('clearance.dock.schemaTooNew') : error.message}
              labels={{ failed: t('clearance.dock.failed') }}
            />
            {/* A schema from the future is not a transient failure: only a
                re-run of the analysis produces a file this client can read, so
                offering "Try again" would be offering a button that cannot
                work. */}
            {!schemaTooNew && (
              <Button data-testid='clearance-retry' type='button' size='sm' variant='outline' onClick={refetch}>
                {t('clearance.dock.retry')}
              </Button>
            )}
          </div>
        )}
        {/* Finding I2: the same multi-megabyte byproduct fetch the sibling dock
            reports with bytes and a progress bar. `useClearance` was already
            streaming `progress`; this is where it stops being dead work. */}
        {!error && isLoading && !json && (
          <LoadingBadge
            name={badgeName}
            phase={progress?.phase ?? 'fetching'}
            progress={progress?.totalBytes ? progress.loadedBytes / progress.totalBytes : undefined}
            loadedBytes={progress?.loadedBytes}
            totalBytes={progress?.totalBytes ?? undefined}
            labels={{
              fetching: t('clearance.dock.fetching'),
              decoding: t('clearance.dock.decoding'),
              failed: t('clearance.dock.failed'),
            }}
          />
        )}
        {/* Finding I12: a clean sweep is the GOOD outcome and the one most
            worth stating plainly. Without this the reader is left inferring it
            from an axis with no bars and a header-only table. Keyed off the
            RUN's own violation count, not the brushed totals — "this run found
            nothing anywhere" is a different claim from "nothing in the range
            you have selected", and the KPI row above already carries the
            second. The chart and table stay mounted underneath: a clean run
            can still have a near-miss ladder worth reading. */}
        {!error && json && json.summary.violationCount === 0 && (
          <p
            data-testid='clearance-no-violations'
            className='mb-2 rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
          >
            {t('clearance.dock.noViolations')}
          </p>
        )}
        {!error && json && dock.tab === 'chart' && (
          <div ref={plotRef} className='min-h-0 flex-1'>
            <ClearanceChart
              json={json}
              layerId={dock.layerId}
              width={plotBox?.w ?? width - 32}
              height={plotBox?.h ?? DEFAULT_PLOT_H}
              metric={metric}
              onMetricChange={setMetric}
            />
          </div>
        )}
        {!error && json && dock.tab === 'table' && (
          <div className='min-h-0 flex-1 overflow-auto'>
            <ClearanceTable json={json} layerId={dock.layerId} />
          </div>
        )}
        {/* Finding F-7: `meta.byproducts.json` is typed optional and every
            branch above is guarded on `json`, so a run without a registered
            JSON byproduct previously rendered an empty box with no message —
            the CSV link already says "pending" for the equivalent gap
            (`meta.byproducts.csv && …` above), this is its counterpart. Not
            reachable through a successful run today (the worker registers the
            JSON or the postprocess handler throws), but the type says it can
            happen, so the UI should not go silent when it does. */}
        {!error && !isLoading && !json && (
          <span data-testid='clearance-no-data' className='text-sm text-slate-500 dark:text-stone-400'>
            {t('clearance.dock.noData')}
          </span>
        )}
        {!error && json && dock.tab === 'profile' && (
          <div ref={plotRef} className='flex min-h-0 flex-1 items-stretch gap-4'>
            {/* The MEASURED cross-section when the run carries the section
                grid; the static sketch is what a run from a worker older than
                that byproduct still gets, so an old analysis degrades to
                exactly what it always showed rather than to an error. */}
            <ClearanceSectionChart
              json={json}
              meta={meta}
              batchId={batchId}
              binIndex={sectionBinIndex}
              onStepBin={stepSectionBin}
              width={Math.max(240, plotBox?.w ?? width - 32)}
              height={Math.max(160, plotBox?.h ?? DEFAULT_PLOT_H)}
              active={dock.tab === 'profile'}
              fallback={
                <div className='flex items-start gap-4'>
                  <ClearanceProfileSketch
                    vertices={json.profile.vertices}
                    verticalOffsetM={json.params.verticalOffsetM}
                    widthPx={320}
                    heightPx={Math.max(160, plotBox?.h ?? DEFAULT_PLOT_H)}
                    highlight={
                      selectedBin && selectedBin.worstOffsetM !== null && selectedBin.worstHeightM !== null
                        ? { offsetM: selectedBin.worstOffsetM, heightM: selectedBin.worstHeightM }
                        : null
                    }
                  />
                  <p className='max-w-md text-xs text-slate-600 dark:text-stone-300'>
                    {selectedBin
                      ? t('clearance.dock.profileSelected', {
                          station: staTag((selectedBin.staStartDisplayM + selectedBin.staEndDisplayM) / 2, 2),
                          depth: selectedBin.maxIntrusionM === null ? '—' : formatFixed(selectedBin.maxIntrusionM, 3),
                        })
                      : t('clearance.dock.profileHint')}
                  </p>
                </div>
              }
            />
          </div>
        )}
      </div>

      {/* No footer. It carried exactly one sentence — how the run was measured
          — restated from the parameters, in a strip the reader had to look
          away from the KPIs to read. It is the chips row in the header now,
          beside the name of the run it parameterises. */}
    </div>
  );
};
